import { useState, useEffect } from "react";
import DashboardDoctorHeader from "../../components/MainHeader/DashboardDoctorHeader";
import Sidebar from "../../components/Sidebar";
import { useUserContext } from "../../context/UserContext";
import LoadingPage from "../../components/LoadingPage";
import { getSubscriptionHistoryApi, createSubscriptionCheckoutApi, getSubscriptionPlansApi, getSubscriptionCurrentApi } from "../../services/apiService";
import "./Subscription.scss";

const defaultPlans = [
  { name: "1 mois", id: "1m", months: 1, price: 6500, displayPrice: "6 500 DA", monthly: "6 500 DA/mois", savings: null, description: "Idéal pour découvrir la plateforme", popular: false, buttonStyle: "secondary" },
  { name: "3 mois", id: "3m", months: 3, price: 18000, displayPrice: "18 000 DA", monthly: "6 000 DA/mois", savings: "Économisez 1 500 DA", description: "Pour les utilisateurs réguliers", popular: false, buttonStyle: "secondary" },
  { name: "6 mois", id: "6m", months: 6, price: 33000, displayPrice: "33 000 DA", monthly: "5 500 DA/mois", savings: "Économisez 6 000 DA", description: "Le bon équilibre", popular: false, buttonStyle: "secondary" },
  { name: "12 mois", id: "12m", months: 12, price: 58000, originalPrice: "78 000 DA", displayPrice: "58 000 DA", monthly: "4 833 DA/mois", savings: "Économisez 20 000 DA", bonus: "🎁 Plus de 3 mois offerts", description: "Recommandé pour les cabinets et cliniques", popular: true, buttonStyle: "primary" },
];

const paymentMethods = [
  { id: "cib", name: "Carte CIB", sub: "Carte interbancaire algérienne", tag: "Recommandé", logo: "CIB", bg: "#00337A", fg: "#FFFFFF" },
  { id: "dahabiya", name: "El Dahabiya", sub: "Carte de paiement Algérie Poste", tag: null, logo: "DAH", bg: "#C8960C", fg: "#FFFFFF" },
];

export default function Subscription() {
  const { sidebarDrawerOpen } = useUserContext();
  const [tab, setTab] = useState("subscribe");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(() => {
    // Load saved payment method from localStorage
    return localStorage.getItem("selectedPaymentMethod") || "cib";
  });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState("");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [plans, setPlans] = useState(defaultPlans);

  const normalizeDaysLeft = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, parsed);
  };

  const calculateDaysLeft = (endDate) => {
    if (!endDate) return 0;

    const end = new Date(`${endDate}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (Number.isNaN(end.getTime())) return 0;
    return Math.max(0, Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, historyRes, currentRes] = await Promise.all([
        getSubscriptionPlansApi(),
        getSubscriptionHistoryApi(),
        getSubscriptionCurrentApi()
      ]);

      if (plansRes.data?.success && plansRes.data?.plans) {
            const apiPlans = Object.entries(plansRes.data.plans).map(([key, plan]) => {
                const defaultPlan = defaultPlans.find(p => p.id === key);
                return {
                    ...defaultPlan,
                    id: key,
                    name: plan.label,
                    price: plan.amount,
                    displayPrice: `${plan.amount.toLocaleString('fr-FR').replace(/\s/g, ' ')} DA`,
                    monthly: `${Math.round(plan.amount / plan.duration_months).toLocaleString('fr-FR')} DA/mois`
                };
            });
            setPlans(apiPlans.length > 0 ? apiPlans : defaultPlans);
        }

      if (historyRes.data?.success && historyRes.data?.data) {
        setPaymentHistory(historyRes.data.data);
      }

      if (currentRes.data?.success && currentRes.data?.data) {
        const current = currentRes.data.data;
        setSubscriptionInfo({
          from_date: current.starts_at,
          to_date: current.ends_at,
          is_active: true,
          plan_label: current.plan,
          days_left: calculateDaysLeft(current.ends_at),
          is_free: current.is_free
        });
      } else {
        // Find active subscription from history as fallback
        const active = (historyRes.data?.data || []).find(h => 
          (h.status === "paid" || h.status === "free") && h.ends_at
        );
        if (active) {
          // Calculate days left from fallback
          const endsAt = new Date(active.ends_at);
          const today = new Date();
          const diffTime = endsAt - today;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          setSubscriptionInfo({
            from_date: active.starts_at,
            to_date: active.ends_at,
            is_active: true,
            plan_label: active.plan,
            days_left: normalizeDaysLeft(diffDays),
            is_free: active.status === "free"
          });
        } else {
          setSubscriptionInfo({
            from_date: null,
            to_date: null,
            is_active: false,
            days_left: 0,
            is_free: false
          });
        }
      }

    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
      setPlans(defaultPlans);
      setSubscriptionInfo({
        from_date: null,
        to_date: null,
        is_active: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Save selected payment method to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("selectedPaymentMethod", selectedMethod);
  }, [selectedMethod]);

  const handlePayment = async () => {
    if (!selectedPlan) return;

    try {
      setIsProcessing(true);
      setMessage("");

      const res = await createSubscriptionCheckoutApi({
        plan_id: selectedPlan,
        payment_method: selectedMethod
      });

      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }

      if (res.data?.success) {
        setMessage("Paiement effectué avec succès !");
        await fetchData();
      }
    } catch (error) {
      console.error("Erreur lors du paiement:", error);
      setMessage(error.response?.data?.message || "Erreur lors du paiement. Veuillez réessayer.");
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const remainingDays = normalizeDaysLeft(subscriptionInfo?.days_left ?? 0);
  const shouldShowExpiringSoon = subscriptionInfo?.is_active && remainingDays > 0 && remainingDays <= 5;

  const filteredHistory = filter === "all" 
    ? paymentHistory 
    : filter === "paid" 
      ? paymentHistory.filter(h => h.status === "paid" || h.status === "free") 
      : paymentHistory.filter(h => h.status === filter);

  if (loading) {
    return (
      <div className="doctormain">
        <Sidebar />
        <div className={`rightSec ${sidebarDrawerOpen ? "rightOpenSideMenu" : ""}`}>
          <DashboardDoctorHeader hideWelcome={true} hideSearch={true} hideProfile={true} />
          <div className="mainDiv" style={{ padding: "50px", textAlign: "center" }}>
            <LoadingPage />
          </div>
        </div>
      </div>
    );
  }

  const activePlan = plans.find(p => p.id === selectedPlan);

  return (
    <div className="doctormain">
      <Sidebar />
      <div className={`rightSec ${sidebarDrawerOpen ? "rightOpenSideMenu" : ""}`}>
        <DashboardDoctorHeader hideWelcome={true} hideSearch={true} hideProfile={true} />
        <div className="mainDiv">
          <div className="pr-root">
            <div className="pr-inner">
              <div className="pr-hdr">
                <h1 className="pr-title">Choisissez votre abonnement</h1>
                <p className="pr-sub">Plus vous vous engagez longtemps, plus vous économisez.</p>
              </div>

              {subscriptionInfo && (
                <div className="checkout mb-10">
                  <div className="sec-lbl" style={{ marginTop: "0" }}>État actuel de l'abonnement</div>
                  
                  {/* Warning notification if expiring soon */}
                  {shouldShowExpiringSoon && (
                    <div style={{
                      padding: "16px",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      backgroundColor: "#fffbe6",
                      borderLeft: "4px solid #faad14",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px"
                    }}>
                      <span style={{ fontSize: "24px" }}>⚠️</span>
                      <div>
                        <div style={{ fontWeight: 600, color: "#ad6800" }}>
                          Votre abonnement expire bientôt !
                        </div>
                        <div style={{ color: "#ad6800", fontSize: "14px" }}>
                          Il reste seulement <strong>{remainingDays} jour{remainingDays > 1 ? "s" : ""}</strong> avant la date limite.
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="sum-grid">
                    <div className="sum-item">
                      <div className="sum-lbl">Date de début</div>
                      <div className="sum-val">{formatDate(subscriptionInfo.from_date)}</div>
                    </div>
                    <div className="sum-item">
                      <div className="sum-lbl">Date de fin</div>
                      <div className="sum-val">{formatDate(subscriptionInfo.to_date)}</div>
                    </div>
                    <div className="sum-item">
                      <div className="sum-lbl">Jours restants</div>
                      <div className="sum-val" style={{ 
                        fontWeight: 600, 
                        color: remainingDays <= 5 && remainingDays > 0 ? "#ff4d4f" : "#52c41a" 
                      }}>
                        {remainingDays} jour{remainingDays > 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className="sum-item">
                      <div className="sum-lbl">Statut</div>
                      <div className="sum-val">
                        <span className={`s-badge ${subscriptionInfo.is_active ? "active" : "expired"}`}>
                          {subscriptionInfo.is_active ? "Actif" : "Expiré"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {message && (
                <div style={{
                  padding: "12px 16px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  backgroundColor: message.includes("Erreur") ? "#fff1f0" : "#e6f7ff",
                  color: message.includes("Erreur") ? "#f5222d" : "#52c41a"
                }}>
                  {message}
                </div>
              )}

              <div className="tabs-c">
                <div className="tabs-w">
                  {[
                    { id: "subscribe", label: "S'abonner" },
                    { id: "history", label: "Historique de paiements" }
                  ].map(t => (
                    <button
                      key={t.id}
                      className={`tab-btn ${tab === t.id ? "active" : ""}`}
                      onClick={() => setTab(t.id)}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {tab === "subscribe" && (
                <div>
                  <div className="sec-lbl">Durée d'abonnement</div>
                  <div className="plans-grid">
                    {plans.map(plan => {
                      const isSel = selectedPlan === plan.id;
                      return (
                        <div
                          key={plan.id}
                          className={`plan-card ${isSel ? "selected" : ""} ${plan.popular ? "popular" : ""}`}
                          onClick={() => setSelectedPlan(plan.id)}
                        >
                          {plan.popular && <div className="pop-badge">⭐ Le plus populaire</div>}
                          <div className="pn">{plan.name}</div>
                          <p className="pd">{plan.description}</p>
                          <div className="pp-wrap">
                            {plan.originalPrice && <div className="p-orig">{plan.originalPrice}</div>}
                            <div className="p-price">{plan.displayPrice}</div>
                            <div className="p-mo">{plan.monthly}</div>
                            {plan.savings && <div className="b-save">{plan.savings}</div>}
                            {plan.bonus && <div><div className="b-bonus">{plan.bonus}</div></div>}
                          </div>
                          <ul className="p-feats">
                            <li>✓ Toutes les fonctionnalités</li>
                            <li>✓ Support inclus</li>
                            <li>✓ Mises à jour automatiques</li>
                            {plan.popular && <li className="rec">✓ Offre recommandée</li>}
                          </ul>
                          <button className={`p-btn ${plan.buttonStyle === "primary" ? "pri" : "sec"}`}>
                            {isSel ? "Plan choisi ✓" : "Choisir ce plan"}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="sec-lbl">Moyen de paiement</div>
                  <div className="methods-list">
                    {paymentMethods.map(method => {
                      const isSel = selectedMethod === method.id;
                      return (
                        <div
                          key={method.id}
                          className={`m-row ${isSel ? "selected" : ""}`}
                          onClick={() => setSelectedMethod(method.id)}
                        >
                          <div className="m-radio"><div className="m-dot" /></div>
                          <div className="m-logo" style={{ backgroundColor: method.bg, color: method.fg }}>{method.logo}</div>
                          <div className="m-info">
                            <div className="m-name">{method.name}</div>
                            <div className="m-sub">{method.sub}</div>
                          </div>
                          {method.tag && <span className="m-tag">{method.tag}</span>}
                        </div>
                      );
                    })}
                  </div>

                  <div className="checkout">
                    <div className="sum-grid">
                      <div className="sum-item">
                        <div className="sum-lbl">Plan choisi</div>
                        <div className="sum-val">{activePlan ? `Abonnement ${activePlan.name}` : "Non sélectionné"}</div>
                      </div>
                      <div className="sum-item">
                        <div className="sum-lbl">Durée totale</div>
                        <div className="sum-val">{activePlan ? activePlan.name : "—"}</div>
                      </div>
                      <div className="sum-item">
                        <div className="sum-lbl">Mode de paiement</div>
                        <div className="sum-val">{paymentMethods.find(m => m.id === selectedMethod)?.name || "—"}</div>
                      </div>
                    </div>
                    <div className="co-div" />
                    <div className="tot-row">
                      <span className="tot-lbl">Total à payer</span>
                      <div>
                        <span className="tot-val">{activePlan ? activePlan.displayPrice.replace(" DA", "") : "0"}</span>
                        <span className="tot-cur">DA</span>
                      </div>
                    </div>
                    <button
                      className="cta"
                      disabled={!selectedPlan || isProcessing}
                      onClick={handlePayment}
                    >
                      {isProcessing ? "Traitement en cours..." : "🔒 Confirmer et payer"}
                    </button>
                    <p className="cta-note">
                      <span>🛡️</span> Paiement 100% sécurisé · Facturation immédiate · Sans engagement caché
                    </p>
                  </div>
                </div>
              )}

              {tab === "history" && (
                <div>
                  <div className="hist-filters">
                    {[
                      { id: "all", label: "Tous" },
                      { id: "paid", label: "Actifs" },
                      { id: "failed", label: "Expirés" }
                    ].map(f => (
                      <button
                        key={f.id}
                        className={`f-btn ${filter === f.id ? "active" : ""}`}
                        onClick={() => setFilter(f.id)}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <div className="hist-card">
                    {filteredHistory.length === 0 ? (
                      <div className="hist-empty">Aucun paiement trouvé.</div>
                    ) : (
                      <table className="hist-tbl">
                        <thead>
                          <tr>
                            {["Plan", "Date d'achat", "Validité", "Méthode", "Montant", "Statut"].map(h => (
                              <th key={h} className="h-th">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredHistory.map(h => {
                            const m = paymentMethods.find(method => method.id === h.payment_method);
                            const isActive = h.status === "paid" || h.status === "free";
                            const isFree = h.status === "free";
                            return (
                              <tr key={h.id} className="h-tr">
                                <td className="h-td" style={{ fontWeight: 600 }}>{h.plan}</td>
                                <td className="h-td" style={{ color: "#64748B" }}>{formatDate(h.purchased)}</td>
                                <td className="h-td">
                                  <div>{formatDate(h.starts_at)}</div>
                                  <div style={{ color: "#94A3B8", fontSize: 12 }}>→ {formatDate(h.ends_at)}</div>
                                </td>
                                <td className="h-td">
                                  {m ? (
                                    <div className="mpill">
                                      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 21, borderRadius: 4, backgroundColor: m.bg, color: m.fg, fontSize: 8, fontWeight: 800 }}>{m.logo}</span>
                                      {h.payment_method}
                                    </div>
                                  ) : (
                                    <span style={{ color: "#94A3B8" }}>Gratuit</span>
                                  )}
                                </td>
                                <td className="h-td" style={{ fontWeight: 600 }}>{h.amount}</td>
                                <td className="h-td">
                                  <span className={`s-badge ${isActive ? "active" : "expired"}`}>
                                    {isActive && <span className="pdot" />}
                                    {isActive ? (isFree ? "Essai gratuit" : "Actif") : "Expiré"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
